import { apiFetch, apiFetchBlob } from "@/lib/api";
import type {
  AddressType,
  BulkActionResult,
  BulkClientAction,
  Client,
  ClientComparison,
  ClientLink,
  ClientListMeta,
  ClientListParams,
  ClientStats,
  ClientStatus,
  ClientTag,
  ClientTaskStatus,
  ClientType,
  ComparisonWho,
  ContactMethod,
  CreateClientPayload,
  Dependent,
  DependentPayload,
  Employer,
  EmployerBelongsTo,
  EmployerPayload,
  ImportableField,
  ImportPreview,
  ImportResult,
  IrsStatus,
  LinkClientPayload,
  LinkType,
  MaritalStatus,
  OwnershipInterest,
  PayPeriod,
  UpdateClientPayload,
} from "@/features/clients/types";
import { formatPhone, formatZip } from "@/lib/masks";

/**
 * The Django API wraps every body in a standard envelope
 * (`APIJSONRenderer`): `{ success, message, data }`, plus `meta` for paginated
 * lists. `apiFetch` returns that envelope verbatim, so this layer unwraps
 * `data`/`meta` and maps the snake_case read shape onto the camelCase `Client`.
 */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
  meta?: ClientListMeta;
}

interface ApiClientLink {
  id: string;
  to_client: { id: string; name: string; type: ClientType };
  belongs_to: EmployerBelongsTo;
  link_type: LinkType;
}

/** Read shape returned by the clients endpoints (snake_case). */
interface ApiClient {
  id: string;
  type: ClientType;
  name: string;
  id_label: string;
  email: string;
  country: string;
  case_number: string;
  irs_status: IrsStatus;
  status: string;
  practitioner: string;
  practitioners: { id: string; name: string }[];
  balance_cents: number;
  spouse: { name: string; id_label: string } | null;
  tasks: { label: string; variant: ClientTaskStatus };
  last_activity: string;
  created_at: string;
  updated_at: string;
  age: number | null;
  spouse_age: number | null;
  address_qc: string[];
  links: ApiClientLink[];
  tags?: ClientTag[];
  assigned_members?: { id: string; name: string }[];
  nsi_assigned_members?: { id: string; name: string; bp_code?: string }[];

  // --- editable profile detail (raw; dates ISO or null, phones/zip digits) ---
  first_name: string;
  middle_initial: string;
  last_name: string;
  business_name: string;
  dba: string;
  business_contact_first_name: string;
  business_contact_last_name: string;
  business_contact_title: string;
  in_community_property_state: boolean;
  explanation: string;
  date_of_birth: string | null;
  driver_license: string;
  license_state: string;
  passport_number: string;
  passport_on_file: boolean;
  internal_reference_id: string;
  salutation: string;
  marital_status: MaritalStatus;
  date_of_marriage: string | null;
  is_deceased: boolean;
  date_of_death: string | null;
  trustee_name: string;
  trustee_role: string;
  home_phone: string;
  mobile_phone: string;
  fax_number: string;
  work_phone: string;
  work_mobile: string;
  preferred_contact_method: ContactMethod;

  spouse_first_name: string;
  spouse_middle_initial: string;
  spouse_last_name: string;
  spouse_date_of_birth: string | null;
  spouse_driver_license: string;
  spouse_license_state: string;
  spouse_passport_number: string;
  spouse_passport_on_file: boolean;
  spouse_salutation: string;
  spouse_is_deceased: boolean;
  spouse_date_of_death: string | null;
  spouse_trustee_name: string;
  spouse_trustee_role: string;
  spouse_home_phone: string;
  spouse_mobile_phone: string;
  spouse_fax_number: string;
  spouse_work_phone: string;
  spouse_work_mobile: string;
  spouse_preferred_contact_method: ContactMethod;
  spouse_email: string;

  home_address_type: AddressType;
  home_street: string;
  home_line2: string;
  home_city: string;
  home_state: string;
  home_zip: string;
  home_county: string;
  home_timezone: string;
  home_international_address: string;
  home_country: string;

  mailing_is_different: boolean;
  mailing_address_type: AddressType;
  mailing_street: string;
  mailing_line2: string;
  mailing_city: string;
  mailing_state: string;
  mailing_zip: string;
  mailing_county: string;
  mailing_timezone: string;
  mailing_international_address: string;
  mailing_country: string;
}

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});
const timeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
});

/** ISO timestamp -> "Today, 10:42 AM" / "Yesterday" / "Oct 20, 2023". */
function formatActivity(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayMs = 86_400_000;
  const diffDays = Math.floor((startOfToday.getTime() - date.getTime()) / dayMs);
  if (diffDays <= 0) return `Today, ${timeFormatter.format(date)}`;
  if (diffDays === 1) return "Yesterday";
  return dateFormatter.format(date);
}

function toClient(api: ApiClient): Client {
  return {
    id: api.id,
    name: api.name,
    idLabel: api.id_label,
    type: api.type,
    status: api.status as Client["status"],
    caseNumber: api.case_number,
    irsStatus: api.irs_status,
    practitioner: api.practitioner,
    practitioners: api.practitioners ?? [],
    balanceCents: api.balance_cents,
    tasks: api.tasks,
    lastActivity: formatActivity(api.last_activity),
    spouse: api.spouse ? { name: api.spouse.name, idLabel: api.spouse.id_label } : undefined,
    age: api.age,
    spouseAge: api.spouse_age,
    addressQc: api.address_qc ?? [],
    links: (api.links ?? []).map((link) => ({
      id: link.id,
      toClient: link.to_client,
      belongsTo: link.belongs_to,
      linkType: link.link_type,
    })),
    tags: api.tags ?? [],
    assignedMembers: api.assigned_members ?? [],
    nsiAssignedMembers: api.nsi_assigned_members ?? [],

    country: api.country,
    firstName: api.first_name,
    middleInitial: api.middle_initial,
    lastName: api.last_name,
    businessName: api.business_name,
    dba: api.dba,
    businessContactFirstName: api.business_contact_first_name,
    businessContactLastName: api.business_contact_last_name,
    businessContactTitle: api.business_contact_title,
    inCommunityPropertyState: api.in_community_property_state,
    explanation: api.explanation,
    email: api.email,
    dateOfBirth: api.date_of_birth ?? "",
    driverLicense: api.driver_license,
    licenseState: api.license_state,
    passportNumber: api.passport_number,
    passportOnFile: api.passport_on_file,
    internalReferenceId: api.internal_reference_id,
    salutation: api.salutation,
    maritalStatus: api.marital_status,
    dateOfMarriage: api.date_of_marriage ?? "",
    isDeceased: api.is_deceased,
    dateOfDeath: api.date_of_death ?? "",
    trusteeName: api.trustee_name,
    trusteeRole: api.trustee_role,
    homePhone: api.home_phone,
    mobilePhone: api.mobile_phone,
    faxNumber: api.fax_number,
    workPhone: api.work_phone,
    workMobile: api.work_mobile,
    preferredContactMethod: api.preferred_contact_method,

    spouseFirstName: api.spouse_first_name,
    spouseMiddleInitial: api.spouse_middle_initial,
    spouseLastName: api.spouse_last_name,
    spouseDateOfBirth: api.spouse_date_of_birth ?? "",
    spouseDriverLicense: api.spouse_driver_license,
    spouseLicenseState: api.spouse_license_state,
    spousePassportNumber: api.spouse_passport_number,
    spousePassportOnFile: api.spouse_passport_on_file,
    spouseSalutation: api.spouse_salutation,
    spouseIsDeceased: api.spouse_is_deceased,
    spouseDateOfDeath: api.spouse_date_of_death ?? "",
    spouseTrusteeName: api.spouse_trustee_name,
    spouseTrusteeRole: api.spouse_trustee_role,
    spouseHomePhone: api.spouse_home_phone,
    spouseMobilePhone: api.spouse_mobile_phone,
    spouseFaxNumber: api.spouse_fax_number,
    spouseWorkPhone: api.spouse_work_phone,
    spouseWorkMobile: api.spouse_work_mobile,
    spousePreferredContactMethod: api.spouse_preferred_contact_method,
    spouseEmail: api.spouse_email,

    homeAddressType: api.home_address_type,
    homeStreet: api.home_street,
    homeLine2: api.home_line2,
    homeCity: api.home_city,
    homeState: api.home_state,
    homeZip: api.home_zip,
    homeCounty: api.home_county,
    homeTimezone: api.home_timezone,
    homeInternationalAddress: api.home_international_address,
    homeCountry: api.home_country,

    mailingIsDifferent: api.mailing_is_different,
    mailingAddressType: api.mailing_address_type,
    mailingStreet: api.mailing_street,
    mailingLine2: api.mailing_line2,
    mailingCity: api.mailing_city,
    mailingState: api.mailing_state,
    mailingZip: api.mailing_zip,
    mailingCounty: api.mailing_county,
    mailingTimezone: api.mailing_timezone,
    mailingInternationalAddress: api.mailing_international_address,
    mailingCountry: api.mailing_country,
  };
}

function buildQuery(params: ClientListParams): string {
  const q = new URLSearchParams();
  if (params.page) q.set("page", String(params.page));
  if (params.pageSize) q.set("page_size", String(params.pageSize));
  if (params.search) q.set("search", params.search);
  if (params.type) q.set("type", params.type);
  if (params.status) q.set("status", params.status);
  if (params.practitioner) q.set("practitioner", params.practitioner);
  // Tag filter is repeatable server-side (any-match), so append each id.
  for (const tagId of params.tag ?? []) q.append("tag", tagId);
  if (params.firm) q.set("firm", params.firm);
  const s = q.toString();
  return s ? `?${s}` : "";
}

export interface ClientListResult {
  clients: Client[];
  meta: ClientListMeta;
}

/** GET /clients/ — paginated, firm-scoped list. */
export async function listClients(params: ClientListParams = {}): Promise<ClientListResult> {
  const body = await apiFetch<Envelope<ApiClient[]>>(`/clients/${buildQuery(params)}`);
  return {
    clients: body.data.map(toClient),
    meta: body.meta ?? { count: body.data.length, next: null, previous: null },
  };
}

/** GET /clients/:id/ */
export async function getClient(id: string): Promise<Client> {
  const body = await apiFetch<Envelope<ApiClient>>(`/clients/${id}/`);
  return toClient(body.data);
}

/** POST /clients/ — pass `firm` to create under a specific firm (a GA/SA or
 * house CD acting on behalf of a BP). The firm rides on the query string, never
 * the body, matching the server contract (firm is bound from the request). */
export async function createClient(
  payload: CreateClientPayload,
  firm?: string,
): Promise<Client> {
  const path = firm ? `/clients/?firm=${encodeURIComponent(firm)}` : "/clients/";
  const body = await apiFetch<Envelope<ApiClient>>(path, {
    method: "POST",
    json: payload,
  });
  return toClient(body.data);
}

/** PATCH /clients/:id/ */
export async function updateClient(id: string, payload: UpdateClientPayload): Promise<Client> {
  const body = await apiFetch<Envelope<ApiClient>>(`/clients/${id}/`, {
    method: "PATCH",
    json: payload,
  });
  return toClient(body.data);
}

/** DELETE /clients/:id/ — soft-deletes server-side. */
export function deleteClient(id: string): Promise<void> {
  return apiFetch<void>(`/clients/${id}/`, { method: "DELETE" });
}

/** POST /clients/:id/deactivate/ — status → INACTIVE. Returns the fresh client. */
export async function deactivateClient(id: string): Promise<Client> {
  const body = await apiFetch<Envelope<ApiClient>>(`/clients/${id}/deactivate/`, {
    method: "POST",
  });
  return toClient(body.data);
}

/** POST /clients/:id/reactivate/ — status → ACTIVE. Returns the fresh client. */
export async function reactivateClient(id: string): Promise<Client> {
  const body = await apiFetch<Envelope<ApiClient>>(`/clients/${id}/reactivate/`, {
    method: "POST",
  });
  return toClient(body.data);
}

/** POST /clients/:id/link/ — cross-link this client to an opposite-type client. */
export async function linkClient(id: string, payload: LinkClientPayload): Promise<ClientLink> {
  const body = await apiFetch<Envelope<ApiClientLink>>(`/clients/${id}/link/`, {
    method: "POST",
    json: payload,
  });
  return {
    id: body.data.id,
    toClient: body.data.to_client,
    belongsTo: body.data.belongs_to,
    linkType: body.data.link_type,
  };
}

/** POST /clients/:id/unlink/ — remove the cross-link to `toClientId`. */
export function unlinkClient(id: string, toClientId: string): Promise<void> {
  return apiFetch<void>(`/clients/${id}/unlink/`, {
    method: "POST",
    json: { to_client: toClientId },
  });
}

interface ApiComparison {
  tax_year: number | null;
  who: ComparisonWho;
  available_years: number[];
  rows: {
    category: string;
    reported_to_irs: number;
    tax_return_filed: number;
    difference: number;
  }[];
  pending: boolean;
}

/** GET /clients/:id/comparison/ — WAID (reported-to-IRS) vs filed-return income
 * comparison, per category, for one tax year/filer. */
export async function getClientComparison(
  id: string,
  params: { year?: number; who?: ComparisonWho } = {},
): Promise<ClientComparison> {
  const q = new URLSearchParams();
  if (params.year) q.set("year", String(params.year));
  if (params.who) q.set("who", params.who);
  const query = q.toString() ? `?${q.toString()}` : "";
  const body = await apiFetch<Envelope<ApiComparison>>(`/clients/${id}/comparison/${query}`);
  return {
    taxYear: body.data.tax_year,
    who: body.data.who,
    availableYears: body.data.available_years,
    rows: body.data.rows.map((row) => ({
      category: row.category,
      reportedToIrs: row.reported_to_irs,
      taxReturnFiled: row.tax_return_filed,
      difference: row.difference,
    })),
    pending: body.data.pending,
  };
}

function comparisonQuery(params: { year?: number; who?: ComparisonWho; kind?: "pdf" | "xlsx" }) {
  const q = new URLSearchParams();
  if (params.year) q.set("year", String(params.year));
  if (params.who) q.set("who", params.who);
  if (params.kind) q.set("kind", params.kind);
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** GET the income comparison as a PDF (`kind=pdf`) or Excel (`kind=xlsx`) blob. */
export function downloadComparison(
  id: string,
  kind: "pdf" | "xlsx",
  params: { year?: number; who?: ComparisonWho } = {},
): Promise<Blob> {
  return apiFetchBlob(`/clients/${id}/comparison/export/${comparisonQuery({ ...params, kind })}`);
}

/** POST — render the comparison PDF and save it to the client's Documents. */
export async function archiveComparison(
  id: string,
  params: { year?: number; who?: ComparisonWho } = {},
): Promise<{ documentId: string }> {
  const res = await apiFetch<Envelope<{ document_id: string }>>(
    `/clients/${id}/comparison/archive/${comparisonQuery(params)}`,
    { method: "POST" },
  );
  return { documentId: res.data.document_id };
}

interface ApiClientStats {
  total: number;
  active_cases: number;
  businesses: number;
  individuals: number;
  pending_onboarding: number;
  requiring_attention: number;
}

/** GET /clients/stats/ — summary-card counts; pass `firm` to scope to one firm's
 * book (a GA/SA or house CD acting on behalf of a BP), matching the list. */
export async function getClientStats(firm?: string): Promise<ClientStats> {
  const path = firm ? `/clients/stats/?firm=${encodeURIComponent(firm)}` : "/clients/stats/";
  const body = await apiFetch<Envelope<ApiClientStats>>(path);
  return {
    total: body.data.total,
    activeCases: body.data.active_cases,
    businesses: body.data.businesses,
    individuals: body.data.individuals,
    pendingOnboarding: body.data.pending_onboarding,
    requiringAttention: body.data.requiring_attention,
  };
}

/**
 * GET /clients/export/ — CSV of every client matching the current filters
 * (pagination is ignored server-side, so page params are stripped here).
 * Returns the raw CSV text; tax IDs arrive masked from the server.
 */
export function exportClientsCsv(params: ClientListParams = {}): Promise<string> {
  const { page: _page, pageSize: _pageSize, ...filters } = params;
  return apiFetch<string>(`/clients/export/${buildQuery(filters)}`);
}

/** POST /clients/bulk/ — soft-delete or restatus a batch of clients. */
export async function bulkClientAction(
  ids: string[],
  action: BulkClientAction,
  status?: ClientStatus,
): Promise<BulkActionResult> {
  const body = await apiFetch<Envelope<BulkActionResult>>("/clients/bulk/", {
    method: "POST",
    json: {
      ids,
      action,
      // The API stores uppercase status values ("ACTIVE"), the UI filter type
      // is lowercase ("active").
      ...(status ? { status: status.toUpperCase() } : {}),
    },
  });
  return body.data;
}

interface ApiImportPreview {
  columns: string[];
  fields: { key: string; label: string; requirement: string; sensitive: boolean }[];
  suggested_mapping: Record<string, string>;
  sample_rows: Record<string, string>[];
  row_count: number;
  detected_types: { individual: boolean; business: boolean };
}

interface ApiImportResult {
  created: number;
  row_count: number;
  errors: { row: number; errors: Record<string, string[]> }[];
  failed_csv: string;
}

/** POST /clients/import/preview/ — parse a CSV and get its columns + a
 * suggested field mapping, so the UI can render a mapping step before saving. */
export async function previewImportCsv(file: File): Promise<ImportPreview> {
  const form = new FormData();
  form.append("file", file);
  const body = await apiFetch<Envelope<ApiImportPreview>>("/clients/import/preview/", {
    method: "POST",
    form,
  });
  const d = body.data;
  return {
    columns: d.columns,
    fields: d.fields.map((f) => ({
      key: f.key,
      label: f.label,
      requirement: f.requirement as ImportableField["requirement"],
      sensitive: f.sensitive,
    })),
    suggestedMapping: d.suggested_mapping,
    sampleRows: d.sample_rows,
    rowCount: d.row_count,
    detectedTypes: d.detected_types,
  };
}

/** POST /clients/import/ — upload a CSV; every created client is assigned
 * `practitionerId` as its primary practitioner. Pass `mapping` (fieldKey→csv
 * column, from the preview step) to map arbitrary headers; unmapped columns are
 * skipped. */
export async function importClientsCsv(
  file: File,
  practitionerId: string,
  mapping?: Record<string, string>,
): Promise<ImportResult> {
  const form = new FormData();
  form.append("file", file);
  form.append("practitioner_id", practitionerId);
  if (mapping) form.append("mapping", JSON.stringify(mapping));
  const body = await apiFetch<Envelope<ApiImportResult>>("/clients/import/", {
    method: "POST",
    form,
  });
  const d = body.data;
  return { created: d.created, rowCount: d.row_count, errors: d.errors, failedCsv: d.failed_csv };
}

// --- Client sub-records: dependents & employers ------------------------------

interface ApiDependent {
  id: string;
  name: string;
  age: number | null;
  relationship: string;
  claimed_on_return: boolean;
  contributes_to_household: boolean;
}

interface ApiEmployer {
  id: string;
  belongs_to: EmployerBelongsTo;
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  phone: string;
  occupation: string;
  pay_period: PayPeriod;
  years_employed: number | null;
  months_employed: number | null;
  ok_to_contact_at_work: boolean;
  ownership_interest: OwnershipInterest;
}

function toDependent(api: ApiDependent): Dependent {
  return {
    id: api.id,
    name: api.name,
    age: api.age,
    relationship: api.relationship,
    claimedOnReturn: api.claimed_on_return,
    contributesToHousehold: api.contributes_to_household,
  };
}

function toEmployer(api: ApiEmployer): Employer {
  return {
    id: api.id,
    belongsTo: api.belongs_to,
    name: api.name,
    street: api.street,
    city: api.city,
    state: api.state,
    zip: formatZip(api.zip),
    county: api.county,
    phone: formatPhone(api.phone),
    occupation: api.occupation,
    payPeriod: api.pay_period,
    yearsEmployed: api.years_employed,
    monthsEmployed: api.months_employed,
    okToContactAtWork: api.ok_to_contact_at_work,
    ownershipInterest: api.ownership_interest,
  };
}

// A client has at most a handful of each, so one max-size page covers the lot.
const SUBRECORD_PAGE = "?page_size=100";

/** GET /clients/:id/dependents/ */
export async function listDependents(clientId: string): Promise<Dependent[]> {
  const body = await apiFetch<Envelope<ApiDependent[]>>(
    `/clients/${clientId}/dependents/${SUBRECORD_PAGE}`,
  );
  return body.data.map(toDependent);
}

/** POST /clients/:id/dependents/ */
export async function createDependent(
  clientId: string,
  payload: DependentPayload,
): Promise<Dependent> {
  const body = await apiFetch<Envelope<ApiDependent>>(`/clients/${clientId}/dependents/`, {
    method: "POST",
    json: payload,
  });
  return toDependent(body.data);
}

/** PATCH /clients/:id/dependents/:depId/ */
export async function updateDependent(
  clientId: string,
  id: string,
  payload: DependentPayload,
): Promise<Dependent> {
  const body = await apiFetch<Envelope<ApiDependent>>(`/clients/${clientId}/dependents/${id}/`, {
    method: "PATCH",
    json: payload,
  });
  return toDependent(body.data);
}

/** DELETE /clients/:id/dependents/:depId/ — hard delete. */
export function deleteDependent(clientId: string, id: string): Promise<void> {
  return apiFetch<void>(`/clients/${clientId}/dependents/${id}/`, { method: "DELETE" });
}

/** GET /clients/:id/employers/ */
export async function listEmployers(clientId: string): Promise<Employer[]> {
  const body = await apiFetch<Envelope<ApiEmployer[]>>(
    `/clients/${clientId}/employers/${SUBRECORD_PAGE}`,
  );
  return body.data.map(toEmployer);
}

/** POST /clients/:id/employers/ */
export async function createEmployer(
  clientId: string,
  payload: EmployerPayload,
): Promise<Employer> {
  const body = await apiFetch<Envelope<ApiEmployer>>(`/clients/${clientId}/employers/`, {
    method: "POST",
    json: payload,
  });
  return toEmployer(body.data);
}

/** PATCH /clients/:id/employers/:empId/ */
export async function updateEmployer(
  clientId: string,
  id: string,
  payload: EmployerPayload,
): Promise<Employer> {
  const body = await apiFetch<Envelope<ApiEmployer>>(`/clients/${clientId}/employers/${id}/`, {
    method: "PATCH",
    json: payload,
  });
  return toEmployer(body.data);
}

/** DELETE /clients/:id/employers/:empId/ — hard delete. */
export function deleteEmployer(clientId: string, id: string): Promise<void> {
  return apiFetch<void>(`/clients/${clientId}/employers/${id}/`, { method: "DELETE" });
}
